import { type TaskOrRoundMarker } from 'engine/store';
import { TaskMarker as TaskMarkerComponent } from './TaskMarker';
import { Navigation2 } from 'lucide-react';
import { Heading } from 'components/Heading';
import { useEffect, useState } from 'react';
import { Map, Marker, useMap } from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

const mapStyle: 'bright' | 'liberty' /*liberty is 3d where possible*/ | 'positron' | 'fiord' | 'dark' = 'bright';

export function EventMap({
	taskMarkers,
	playerLocation,
}: {
	taskMarkers: TaskOrRoundMarker[];
	playerLocation: { lat: number; lng: number; heading: number | null };
}) {
	// const navigate = useNavigate();
	// const rounds = useStore((s) => s.game?.rounds);

	const isInvalidLocation = playerLocation.lat === 0 && playerLocation.lng === 0;
	const mapCenter = isInvalidLocation ? { lat: 51.60065270877547, lng: 5.611685495620504, heading: null } : playerLocation; // fallback: Uitjesbazen
	const defaultZoomPoint = 18;

	const [mapCenterPoint, setMapCenterPoint] = useState<{ lng: number; lat: number; heading?: null | number }>(mapCenter);
	const [mapZoomPoint, setMapZoomPoint] = useState(defaultZoomPoint);

	const [centerButtonVisible, setCenterButtonVisible] = useState(false);

	useEffect(() => {
		setCenterButtonVisible(
			mapCenterPoint.lng != mapCenter.lng || mapCenterPoint.lat != mapCenter.lat || mapZoomPoint != defaultZoomPoint,
		);
		if (!centerButtonVisible && playerLocation.lat === 0 && playerLocation.lng === 0) {
			setMapCenterPoint(playerLocation);
		}
	}, [mapCenterPoint.lng, mapCenterPoint.lat, mapCenter.lng, mapCenter.lat, mapZoomPoint, centerButtonVisible, playerLocation]);

	return (
		<div className="size-full">
			<Map
				initialViewState={{
					longitude: mapCenter.lng,
					latitude: mapCenter.lat,
					zoom: defaultZoomPoint,
				}}
				onMoveEnd={(evt) => {
					const { latitude: lat, longitude: lng } = evt.viewState;
					setMapCenterPoint({ lat, lng });
					setCenterButtonVisible(true);
				}}
				onZoomEnd={(evt) => setMapZoomPoint(evt.viewState.zoom)}
				mapStyle={`https://tiles.openfreemap.org/styles/${mapStyle}`}
			>
				{centerButtonVisible && (
					<MapControls
						panLocation={mapCenter}
						defaultZoom={defaultZoomPoint}
						onCenter={() => {
							setMapCenterPoint(mapCenter);
							setMapZoomPoint(defaultZoomPoint);
						}}
					/>
				)}
				<PlayerMarker location={playerLocation} />
				{taskMarkers.map((marker) => (
					<TaskMarkerComponent
						infoWindow={
							// TODO: Prefer Para, but that will not accept text-black, fix InfoWindow to use style
							<>
								{/* <button
										// onClick={() => {
										// 	if (!('task' in marker)) return;
										// 	const round = rounds?.find((r) => r.tasks.some((t) => t.id == marker.taskId));
										// 	if (!round) return;
										// TODO: only navigate if task is unlocked
										// 	navigate({
										// 		to: '/rounds/$roundId/task/$taskId',
										// 		replace: true,
										// 		params: { roundId: round.id, taskId: marker.taskId },
										// 	});
										// }}
									> */}
								<Heading className="text-black">{'task' in marker ? marker.task.title : marker.round.title}</Heading>
								{/* </button> */}
							</>
						}
						unlocked={marker.unlocked}
						position={{
							lat: marker.lat,
							lng: marker.lng,
						}}
						key={'taskId' in marker ? marker.taskId : marker.roundId}
						radius={marker.radius}
						// displayName={marker.displayName}
					/>
				))}
			</Map>
		</div>
	);
}

function MapControls({
	panLocation,
	defaultZoom,
	onCenter,
}: {
	panLocation: { lat: number; lng: number; heading?: number | null };
	defaultZoom: number;
	onCenter?: () => void;
}) {
	const { current: map } = useMap();

	const onClick = () => {
		if (!map) return;
		map.setCenter(panLocation);
		map.setZoom(defaultZoom);
		map.setBearing(0);
		map.setPitch(0);
		onCenter?.();
	};

	return (
		<div className="absolute bottom-12 z-10 ml-5 md:bottom-8">
			<button
				className={`flex w-fit items-center gap-2 rounded-full bg-gray-50 px-5 py-4 text-green-700 outline outline-1 outline-gray-400`}
				onClick={onClick}
			>
				<Navigation2 className="inline" />
				<p className="inline">{__`Center`}</p>
			</button>
		</div>
	);
}

function PlayerMarker({ location }: { location: { lat: number; lng: number; heading: number | null } }) {
	return (
		<Marker longitude={location.lng} latitude={location.lat} anchor="bottom">
			{location.heading != null ? (
				<div className="size-[40px]">
					<Navigation2
						className="size-full fill-red-500 stroke-red-600 drop-shadow-md"
						style={{ transform: `rotate(${location.heading}deg)` }}
					/>
				</div>
			) : (
				<div className="size-[20px]">
					<div className="size-full rounded-full bg-red-500 drop-shadow-md" />
				</div>
			)}
		</Marker>
	);
}
